import { useEffect, useState } from "react";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { LivePage } from "./pages/LivePage.js";
import { BatteryPage } from "./pages/BatteryPage.js";
import { SolarPage } from "./pages/SolarPage.js";
import { AnalyticsPage } from "./pages/AnalyticsPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { SitePage } from "./pages/SitePage.js";
import { TelemetryPage } from "./pages/TelemetryPage.js";
import { SessionsPage } from "./pages/SessionsPage.js";
import { AuditPage } from "./pages/AuditPage.js";
import { AppShell } from "./components/AppShell.js";
import { Failed, Loading, secondaryButtonClass } from "./components/ViewState.js";
import { api } from "./lib/client.js";
import { clearSession, isSignedIn, subscribeToSession } from "./lib/session.js";
import { useAsync } from "./lib/useAsync.js";
import { PreferencesProvider } from "./lib/preferences.js";
import { useRoute } from "./lib/useRoute.js";
import { landingFor, viewForPath, viewsFor, type ViewId } from "./lib/views.js";

export function App() {
  const [signedIn, setSignedIn] = useState(isSignedIn);

  // The session can end without the app asking: a refresh token refused
  // mid-request clears storage from inside the API client. Without this the
  // user is left on a view whose every call now fails.
  useEffect(() => subscribeToSession(() => setSignedIn(isSignedIn())), []);

  // The provider lives here rather than in `main.tsx` so that rendering `App`
  // is enough to have a working app -- which is how every test reaches it, and
  // how the theme class gets onto <html> without the bootstrap having to
  // remember to arrange it.
  return (
    <PreferencesProvider>
      {signedIn ? <AuthenticatedApp /> : <LoginPage onSignedIn={() => setSignedIn(true)} />}
    </PreferencesProvider>
  );
}

/**
 * The signed-in app: which view the URL selects, clamped to what this account
 * may see.
 *
 * The clamp is the rule slice 2 established and slice 5B had to keep when
 * navigation gained URLs. A section the caller lacks permission for is not
 * merely hidden from the navigation, it is not rendered when asked for
 * directly either -- otherwise a demotion mid-session, or a bookmark from
 * before one, leaves them on a blank screen. Every endpoint behind these still
 * enforces its own permission, so this is about not showing an empty view
 * rather than about security.
 *
 * `useRoute` is called exactly once. Calling it in a child hook as well gave
 * two independent copies of the path, each with its own listener, so a nav
 * click updated one and the rendered view read the other.
 */
function AuthenticatedApp() {
  const { state, reload } = useAsync(api.me, "Could not load your account. Try again.");
  const { path, navigate, replace } = useRoute();

  const user = state.kind === "ready" ? state.value : null;
  const available = user === null ? [] : viewsFor(user);
  const landing = landingFor(available);
  const requested = viewForPath(path);
  const permitted =
    requested !== null && available.some((view) => view.id === requested.id) ? requested : null;
  const view = permitted ?? landing;

  // From an effect, not during render: doing it inline set state while another
  // component was rendering. `replace` rather than `navigate`, because the
  // reader did not choose this hop and should not have to press back twice.
  useEffect(() => {
    if (user === null || landing === undefined) return;
    if (permitted === null && path !== landing.path) replace(landing.path);
  }, [user, permitted, landing, path, replace]);

  if (state.kind === "loading") {
    return (
      <Centered>
        <Loading label="Loading…" />
      </Centered>
    );
  }

  if (state.kind === "error") {
    return (
      <Centered>
        <Panel>
          <Failed message={state.message} onRetry={reload} />
          <SignOutButton />
        </Panel>
      </Centered>
    );
  }

  if (view === undefined) {
    return (
      <Centered>
        <Panel>
          <p role="alert" className="text-sm text-ink">
            This account has no sections it may view. Ask an administrator to grant access.
          </p>
          <SignOutButton />
        </Panel>
      </Centered>
    );
  }

  return (
    <AppShell user={state.value} view={view.id} onNavigate={navigate} onSignOut={signOut}>
      <Body view={view.id} permissions={state.value.permissions} />
    </AppShell>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-dvh place-items-center bg-page p-6">{children}</main>;
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex w-full max-w-md flex-col gap-4 rounded-card border border-line bg-card p-6">
      {children}
    </div>
  );
}

function SignOutButton() {
  return (
    <button type="button" onClick={signOut} className={secondaryButtonClass}>
      Sign out
    </button>
  );
}

function signOut(): void {
  // Revokes the session server-side, then clears it locally whether or not
  // that call succeeded; the subscription in App does the rest.
  void api.logout();
}

/** What the reader may do with the tariff table, or null when they may not see it. */
function tariffAccess(permissions: readonly string[]): { canWrite: boolean } | null {
  if (!permissions.includes("tariffs:read")) return null;
  return { canWrite: permissions.includes("tariffs:write") };
}

function Body({ view, permissions }: { view: ViewId; permissions: readonly string[] }) {
  const canWriteSettings = permissions.includes("settings:write");
  switch (view) {
    case "dashboard":
      return <DashboardPage />;
    case "live":
      return <LivePage />;
    case "battery":
      // The same narrower permission the settings form asks for: the battery
      // view carries the reserve-floor control, which writes a setting.
      return <BatteryPage canWrite={canWriteSettings} />;
    case "solar":
      return <SolarPage />;
    case "analytics":
      return <AnalyticsPage />;
    case "settings":
      // The clamp above already refused this view to anyone without
      // `settings:read`. This second permission is the narrower one: it
      // decides whether the page is a form or a list.
      return <SettingsPage canWrite={canWriteSettings} tariffs={tariffAccess(permissions)} />;
    case "telemetry":
      return <TelemetryPage />;
    case "sessions":
      return <SessionsPage onCurrentRevoked={clearSession} />;
    case "audit":
      return <AuditPage />;
    default:
      return <SitePage />;
  }
}
